import './DiscussionShow.scss';

import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import React, { createRef, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import useDeleteDiscussion from 'shared/hooks/discussion-show/useDeleteDiscussion';
import useDiscussion from 'shared/hooks/discussion-show/useDiscussion';
import useLikeDiscussion from 'shared/hooks/discussion-show/useLikeDiscussion';
import useUser from 'shared/hooks/useUser';
import { siteURL } from 'shared/utils/utils';

import { Section } from 'components/Layout';
import useSimpleModal from 'components/modals/SimpleModal';
import Seo from 'components/Seo';
import BackButton from 'components/ui/BackButton';
import RoundButton from 'components/ui/RoundButton';
import ScrollButton from 'components/ui/ScrollButton';
import ShareButton from 'components/ui/ShareButton';
import { ReactComponent as LikeIcon } from 'assets/icons/like.svg';
import cx from 'utils/cx';
import TutorProfileMenu from 'pages/tutor-profile/components/TutorProfileMenu';
import DiscussionComment from './components/DiscussionComment';
import DiscussionCommentInput from './components/DiscussionCommentInput';
import DiscussionShowHeader from './components/DiscussionShowHeader';
import NotFound from '../not-found/NotFound';

const useHighlight = (commentRefs) => {
  const timeoutRef = useRef();

  const highLight = (id) => {
    const ref = commentRefs?.[id];
    if (ref) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
      });
      ref.current.classList.add('highlight');
      if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(() => {
          ref.current.classList.remove('highlight');
          timeoutRef.current = null;
        }, 1000);
      }
    }
  };

  return highLight;
};

const useToggleBodyClass = () => {
  useEffect(() => {
    document.body.classList.add('discussions');
    return () => {
      document.body.classList.remove('discussions');
    };
  }, []);
};

const DiscussionShow = () => {
  const { t } = useTranslation(['common', 'discussion']);
  const history = useHistory();
  const { id } = useParams();
  const { data: discussion, isLoading, isError } = useDiscussion(id);
  const { myId } = useUser();
  useToggleBodyClass();
  const [commentInputPosition, setCommentInputPosition] = useState(null);
  const likeMutation = useLikeDiscussion(id);
  const simpleModal = useSimpleModal();
  const deleteDiscussionMutation = useDeleteDiscussion(id);
  const commentRefs = {};
  const highLight = useHighlight(commentRefs);

  if (isError) {
    return <NotFound />;
  }

  if (isLoading) {
    return <Section />;
  }

  const {
    title,
    attachment,
    discussionContent,
    author,
    isLiked,
    commentCount,
    comments,
  } = discussion;

  const onEdit = () => history.push(`/discussions/${id}/edit`);

  const onDelete = () => {
    simpleModal({
      message: t('discussion:deletePostConfirm'),
      submitText: t('delete'),
      submitColor: 'danger',
    }).then(() =>
      deleteDiscussionMutation.mutate(undefined, {
        onSuccess: () => history.push('/mypage/discussions/myposts'),
      }),
    );
  };

  const getDiscussionContent = () => (
    <div className="discussion-content md-padding">
      <CKEditor
        editor={ClassicEditor}
        data={discussionContent}
        disabled
        config={{ toolbar: [] }}
      />
    </div>
  );

  const getMyActionsButton = () => (
    <div className="btns-wrap md-padding">
      <button type="button" onClick={onEdit}>
        수정
      </button>
      <button type="button" onClick={onDelete}>
        삭제
      </button>
    </div>
  );

  const getFixedButtons = () => (
    <div className="fixed-buttons">
      <ScrollButton />
      <div>
        <RoundButton
          className={cx('like-post', { checked: isLiked })}
          onClick={likeMutation.mutate}
        >
          <LikeIcon />
        </RoundButton>
        <ShareButton
          url={`${siteURL}discussions/${id}`}
          title={title}
          imageUrl={attachment}
        />
      </div>
    </div>
  );

  const getCommentAndSubcomments = (ref, comment) => (
    <Fragment key={comment.id}>
      <li ref={ref}>
        <DiscussionComment
          {...comment}
          commentInputPosition={commentInputPosition}
          setCommentInputPosition={setCommentInputPosition}
        />
      </li>
      {comment.subComments?.map((subComment) => {
        const ref = createRef();
        commentRefs[subComment.id] = ref;

        return (
          <li key={subComment.id} ref={ref}>
            <DiscussionComment
              {...subComment}
              parentId={comment.id}
              commentInputPosition={commentInputPosition}
              setCommentInputPosition={setCommentInputPosition}
              isReply
              highLight={highLight}
            />
          </li>
        );
      })}
    </Fragment>
  );

  return (
    <>
      <Seo title={title} description={discussionContent} image={attachment} />
      <Section className="discussion-index-sec row">
        <BackButton />
        <DiscussionShowHeader {...discussion} />
        <TutorProfileMenu showBlock={false} />
        {getDiscussionContent()}
        {myId === author.id && getMyActionsButton()}
        <div className="discussion-comments">
          <p className="md-padding">댓글({commentCount})</p>
          <ol>
            {comments.map((comment) => {
              const ref = createRef();
              commentRefs[comment.id] = ref;

              return getCommentAndSubcomments(ref, comment);
            })}
          </ol>
          {commentInputPosition === null && (
            <DiscussionCommentInput discussionId={id} />
          )}
        </div>
        {getFixedButtons()}
      </Section>
    </>
  );
};

export default DiscussionShow;
